import React, { useEffect, useState } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { BrowserRouter as Router } from "react-router-dom";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import { reactPlugin } from "./appInsights";
import Login from "./components/Login";
import { ToastContainer } from "react-toastify";
import FullPageLoader from "./common/components/FullPageLoader";
import logger from "./helpers/logger";
import RackStarApp from "./RackStarApp";
import { authRequest } from "./authConfig";
import tokenService from "./services/token-service";
import InstallPrompt from "./components/InstallPrompt";
import "./styles/mui-data-grid.css";

function App({ toggleTheme, isDarkMode }) {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [isReady, setIsReady] = useState(false);
  const [tokenReady, setTokenReady] = useState(false);

  useEffect(() => {
    async function processLoginRedirect() {
      try {
        if (!instance || !instance.initialize) return;
        await instance.initialize();
        const response = await instance.handleRedirectPromise();

        if (response) {
          logger.log("Login redirect successful:", response);
          if (response.accessToken) {
            tokenService.setToken(response.accessToken);
          }
        }
      } catch (error) {
        logger.error("Login redirect failed:", error);
      } finally {
        setIsReady(true);
      }
    }
    processLoginRedirect();
  }, [instance]);

  const getSetToken = async () => {
    try {
      const tokenResponse = await instance.acquireTokenSilent({
        ...authRequest,
        account: accounts[0],
      });

      const accessToken = tokenResponse.accessToken;

      tokenService.setToken(accessToken);

      setTokenReady(true);
      logger.log("Access token set:", accessToken);
    } catch (error) {
      // Handle InteractionRequiredAuthError - MFA/Azure AD session expired
      if (error instanceof InteractionRequiredAuthError) {
        logger.warn("⚠️ App: Session expired (interaction required) - redirecting to login", {
          errorCode: error.errorCode,
          errorMessage: error.errorMessage,
        });
        
        // Trigger interactive login
        try {
          await instance.loginRedirect({
            ...authRequest,
            prompt: "select_account", // Allow user to select account or re-authenticate
          });
        } catch (loginError) {
          logger.error("Failed to redirect to login:", loginError);
          setTokenReady(true); // Set ready to avoid infinite loading
        }
        return;
      }
      
      logger.error("Failed to get token silently:", error);
      setTokenReady(true); // Set ready to avoid infinite loading
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      getSetToken();
    }
  }, [isAuthenticated])

  if (!isReady || (isAuthenticated && !tokenReady)) {
    return <FullPageLoader />;
  }

  return (
    <Router>
      <ToastContainer
        position="bottom-right"
        theme="colored"
        aria-live="polite"
        role="status"
      />
      <InstallPrompt />
      {isAuthenticated ? (
        <RackStarApp
          isDarkMode={isDarkMode}
          toggleTheme={toggleTheme}
        />
      ) : (
        <Login />
      )}
    </Router>
  );
}

export default withAITracking(reactPlugin, App);
